import { AfterViewInit, Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from "@angular/core";
import { CanvasService } from "@app/services/canvas.service";
import { ResponsiveService } from "@app/services/responsive.service";
import { IDriverScore } from "../driver-behaviour-comparison.component";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import "./driver-score-graph.component.scss";

interface ISegment {
    color: string;
    value: number;
}

@UntilDestroy()
@Component({
    selector: "driver-score-graph",
    template: require("./driver-score-graph.component.html")
})
export class DriverScoreGraphComponent implements OnInit, AfterViewInit, OnChanges {
    @Input() score: IDriverScore;
    @Input() width: number;
    @Input() horizontal: boolean;

    @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;

    // ---

    private segments: ISegment[];
    private ctx: CanvasRenderingContext2D;

    private height: number;

    private fontSize: number;
    private messageClass: string;
    private messageString: string;

    private centerX: number;
    private centerY: number;
    private radius: number;
    private lineWidth: number;

    private distancePerTick: number;
    private targetIndicatorPosition: number;
    private currentIndicatorPosition: number;

    constructor(
        private el: ElementRef<HTMLElement>,
        private responsiveService: ResponsiveService,
        private canvasService: CanvasService) {

        this.responsiveService.currentWindowWidth$.pipe(untilDestroyed(this)).subscribe(() => {
            this.resizeCanvas();
            this.drawCanvas();
        });
    }

    ngOnInit() {
    }

    ngAfterViewInit() {
        this.ctx = this.canvas.nativeElement.getContext('2d');

        this.resizeCanvas();
    }

    ngOnChanges(changes: SimpleChanges) {

        if (!this.score) {
            return;
        }

        // Segment configurations

        const b1 = this.score.lowerRagBound / 100;
        var b2 = (this.score.upperRagBound - this.score.lowerRagBound) / 100;
        var b3 = 1 - b2 - b1;
        this.segments = this.createSegments(b1, b2, b3);

        this.onScoreUpdated();
    }

    private onScoreUpdated() {
        // Position for the indicator
        this.currentIndicatorPosition = this.targetIndicatorPosition || 0;

        let targetScore = this.score.score;

        // Clamp scores so we dont bleed off the edge of the canvas
        const buffer = this.horizontal ? 10 : 2;
        Math.min(100 - buffer, this.score.score);
        targetScore = Math.max(buffer, targetScore);
        this.targetIndicatorPosition = targetScore / 100;

        this.distancePerTick = (this.targetIndicatorPosition - this.currentIndicatorPosition) / 33;
        this.distancePerTick = (this.targetIndicatorPosition - this.currentIndicatorPosition) / 50;

        this.animateCanvas();
        this.setMessageClass();
    }

    // ---

    private resizeCanvas() {
        if (!this.canvas?.nativeElement || !this.score) {
            return;
        }

        this.width = this.el.nativeElement.offsetWidth;
        this.fontSize = this.width / 10;

        this.height = this.width / 2;

        if (this.horizontal) {
            this.width = 90;
            this.height = 20;
        }

        this.canvasService.setCanvasDimensions(this.canvas.nativeElement, this.ctx, this.width, this.height);

        // Center and radius of the gauge
        this.centerX = this.canvas.nativeElement.width / 2;
        this.centerY = this.canvas.nativeElement.height * 0.95;
        this.radius = this.canvas.nativeElement.width * 0.4;
        this.lineWidth = this.width / 40; 

        if (this.horizontal) {
            this.centerY = this.canvas.nativeElement.height / 2;
            this.lineWidth = 4;
        }
    }

    private drawCanvas() {
        if (!this.ctx || !this.segments) {
            return;
        }

        this.ctx.clearRect(0, 0, this.width, this.height);

        // Calculate the starting angle and ending angle for each segment
        if (this.horizontal) {
            this.drawHorizontalSegments();
        }
        else {
            this.drawArcSegments();
            this.drawIndicatorDots();
        }

        this.drawIndicator(this.currentIndicatorPosition);
    }

    private animateCanvas() {
        if (!this.canvas?.nativeElement) {
            setTimeout(() => {
                this.animateCanvas();
            });

            return;
        }

        if (!this.height) {
            this.resizeCanvas();
        }

       

        if (Math.abs(this.targetIndicatorPosition - this.currentIndicatorPosition) > Math.abs(this.distancePerTick)) {
            this.currentIndicatorPosition += this.distancePerTick;

            if (this.distancePerTick > 0) {
                this.currentIndicatorPosition = Math.min(this.currentIndicatorPosition, this.targetIndicatorPosition);
            }
            else {
                this.currentIndicatorPosition = Math.max(this.currentIndicatorPosition, this.targetIndicatorPosition);
            }

            requestAnimationFrame(() => { this.animateCanvas(); });
        }
        else {

            this.currentIndicatorPosition = this.targetIndicatorPosition;
        }

        this.drawCanvas();
    }

    private drawHorizontalSegments() {
        let startPosition = 0;
        this.segments.forEach((segment, i) => {
            const endPosition = startPosition + this.width * segment.value;
            this.drawArcSegment(startPosition, endPosition, segment.color, i == 0, i == this.segments.length - 1);
            startPosition = endPosition; // Update the starting angle for the next segment
        });
    }

    private drawArcSegments() {
        let startAngle = Math.PI; // Start at 180 degrees (semi-circle)

        this.segments.forEach((segment, i) => {
            const endAngle = startAngle + Math.PI * segment.value;
            this.drawArcSegment(startAngle, endAngle, segment.color, i == 0, i == this.segments.length - 1);
            startAngle = endAngle; // Update the starting angle for the next segment
        });
    }

    private drawArcSegment(startAngle: number, endAngle: number, color: string, startSegment = false, endSegment = false) {
        this.ctx.beginPath();

        const buffer = this.horizontal ? this.lineWidth / 1.5 : this.lineWidth / 120;
        let angle1 = startAngle;
        let angle2 = endAngle;

        if (!startSegment) {
            angle1 += buffer;
        }

        if (!endSegment) {
            angle2 -= buffer;
        }

        if (this.horizontal) {
            this.ctx.moveTo(startAngle + buffer, this.centerY);
            this.ctx.lineTo(endAngle - buffer, this.centerY);
        }
        else {
            this.ctx.arc(this.centerX, this.centerY, this.radius, angle1, angle2);
        }

        this.ctx.lineWidth = this.lineWidth;
        this.ctx.strokeStyle = color;
        this.ctx.lineCap = 'round';
        this.ctx.stroke();
    }

    //https://dribbble.com/shots/21266211-Dashboard-Elements
    private drawIndicator(position: number) {
        const angle = Math.PI + (Math.PI * position); // Calculate the angle in radians
        let x = this.centerX + this.radius * Math.cos(angle);
        let y = this.centerY + this.radius * Math.sin(angle);

        if (this.horizontal) {
            const buffer = 0.06;
            x = Math.max(0, Math.min(1 - buffer, position)) * this.width;
            y = this.centerY;
        }

        const circleSize = this.lineWidth * 1.1;
        const innerCircleSize = circleSize * 0.6;

        // Draw the outer colored circle
        this.ctx.shadowColor = "black";
        this.ctx.shadowBlur = 3;
        this.ctx.beginPath();
        this.ctx.arc(x, y, circleSize, 0, 2 * Math.PI); // Outer circle radius
        this.ctx.fillStyle = '#FFFFFF'; // Inner circle color
        this.ctx.fill();
        this.ctx.shadowBlur = 0;

        // Draw the inner white circle
        this.ctx.beginPath();
        this.ctx.arc(x, y, innerCircleSize, 0, 2 * Math.PI); // Inner circle radius
        this.ctx.fillStyle = this.getColour(position); // Outer circle color
        this.ctx.fill();
    }

    private drawIndicatorDots() {
        const radius = this.radius * .85;

        this.ctx.globalAlpha = 0.2;

        const numDots = this.width > 300 ? 15 : 10;
        for (let i = 0; i <= numDots; i++) {
            const position = i / numDots;
            const angle = Math.PI + (Math.PI * position); // Calculate the angle in radians
            const x = this.centerX + radius * Math.cos(angle);
            const y = this.centerY + radius * Math.sin(angle);

            // Draw the outer colored circle
            this.ctx.beginPath();
            this.ctx.arc(x, y, 1, 0, 2 * Math.PI); // Outer circle radius
            this.ctx.fillStyle = this.getColour(position); // Outer circle color
            this.ctx.fillStyle = "black";
            this.ctx.fill();
        }

        this.ctx.globalAlpha = 1;
    }

    private createSegments(...values: number[]): ISegment[] {
        var segments = [];
        let runningValue = 0;


        values.forEach(v => {
            runningValue += v;
            segments.push({ color: this.getColour(runningValue), value: v });
        });

        return segments;
    }

    private getColour(value: number): string {

        if (value > this.score.upperRagBound / 100) {
            // green
            return "rgb(92, 167, 147)";
            return '#208840';
        }

        if (value <= this.score.lowerRagBound / 100) {
            // red
            return "rgba(200, 50, 50, .75)";
            return '#cf4946';
        }

        // orange
        return "rgb(236, 170, 56)";
        return 'orange';
    }

    private setMessageClass() {
        this.messageClass = "";
        this.messageString = "Poor";

        if (this.score.score > this.score.upperRagBound) {
            this.messageClass = "good";
            this.messageString = "Great";
        }
        else if (this.score.score > this.score.lowerRagBound) {
            // red
            this.messageClass = "ok";
            this.messageString = "Average";
        }
    }
}
